// Định nghĩa key lưu trữ chuẩn
export const STORAGE = {
  TOKEN: 'token-qlrv_hd',
  USER_INFO: 'user-info-qlrv_hd',
  REMEMBER_LOGIN: 'remember-login',
  DEV_MODE: 'dev-mode',
  REFRESH_TOKEN: 'REFRESH_TOKEN',
  KEY_MENU_ACTIVE: 'key-active',
  IS_SOUND_NOTIF: 'is-sound-notif',
  REMEMBER_HOME: 'remember-home',
} as const

// Type của các key
export type StorageKey = (typeof STORAGE)[keyof typeof STORAGE]

// --------- LocalStorage -----------

export const getLocalStorage = <T = unknown>(key: StorageKey): T | null => {
  const data = localStorage.getItem(key)
  try {
    return data ? JSON.parse(data) : null
  } catch {
    return data as unknown as T
  }
}

export const setLocalStorage = (key: StorageKey, value: unknown): void => {
  const stringify = typeof value === 'string' ? value : JSON.stringify(value)
  localStorage.setItem(key, stringify)
}

export const deleteLocalStorage = (key: StorageKey): void => {
  localStorage.removeItem(key)
}

export const clearLocalStorage = (): void => {
  localStorage.clear()
}

// --------- SessionStorage -----------

export const getSessionStorage = <T = unknown>(key: StorageKey): T | null => {
  if (typeof window === 'undefined') return null
  const data = sessionStorage.getItem(key)
  try {
    return data ? JSON.parse(data) : null
  } catch {
    return data as unknown as T
  }
}

export const setSessionStorage = (key: StorageKey, value: unknown): void => {
  const stringify = typeof value === 'string' ? value : JSON.stringify(value)
  sessionStorage.setItem(key, stringify)
}

export const deleteSessionStorage = (key: StorageKey): void => {
  sessionStorage.removeItem(key)
}

export const clearSessionStorage = (): void => {
  sessionStorage.clear()
}

import { decryptToken, encryptToken } from '../crypto'

// ponytail: setAuthToken/setRefreshToken lưu bản đã encryptToken() — đọc lại phải
// decryptToken() tương ứng, không thì gửi thẳng ciphertext làm Bearer token lên BE.
// STORAGE.REMEMBER_HOME (ghi tại lúc login) là nguồn DUY NHẤT quyết định lưu
// localStorage (remember=true) hay sessionStorage (false). Khi flag thiếu (phiên
// cũ) → fallback giữ nguyên hành vi cũ, không đổi luồng hiện tại.
const getRememberFlag = (): boolean | null => {
  const flag = getLocalStorage<boolean>(STORAGE.REMEMBER_HOME)
  return typeof flag === 'boolean' ? flag : null
}

const readRawToken = (key: StorageKey): string | null => {
  const remember = getRememberFlag()
  const local = getLocalStorage<string>(key)
  const session = getSessionStorage<string>(key)
  if (remember === null) {
    // Legacy: session trước, local sau (hành vi trước khi có flag)
    return session ?? local
  }
  return remember ? (local ?? session) : (session ?? local)
}

const resolveRemember = (remember?: boolean): boolean => {
  const flag = getRememberFlag()
  if (flag !== null) return flag
  if (remember !== undefined) return remember
  // Legacy: flag thiếu → suy từ nơi access token đang nằm => khớp hành vi cũ
  return getLocalStorage<string>(STORAGE.TOKEN) !== null
}

export const getAuthToken = (): string | null => {
  const raw = readRawToken(STORAGE.TOKEN)
  return raw ? decryptToken(raw) : null
}

export const getRefreshToken = (): string | null => {
  // Refresh token luôn ưu tiên đọc localStorage (được set ở cả hai nhánh remember),
  // fallback sessionStorage cho phiên cũ được lưu trước đây.
  const local = getLocalStorage<string>(STORAGE.REFRESH_TOKEN)
  const session = getSessionStorage<string>(STORAGE.REFRESH_TOKEN)
  const raw = local ?? session
  return raw ? decryptToken(raw) : null
}

export const setAuthToken = (token: string, remember?: boolean): void => {
  const encrypted = encryptToken(token)
  if (resolveRemember(remember)) {
    setLocalStorage(STORAGE.TOKEN, encrypted)
    deleteSessionStorage(STORAGE.TOKEN)
  } else {
    setSessionStorage(STORAGE.TOKEN, encrypted)
    deleteLocalStorage(STORAGE.TOKEN)
  }
}

export const setRefreshToken = (token: string, remember?: boolean): void => {
  const encrypted = encryptToken(token)
  // Refresh token LUÔN lưu localStorage kể cả khi remember=false, để tab đóng
  // rồi mở lại vẫn refresh được. Chỉ access token phụ thuộc remember.
  void remember
  setLocalStorage(STORAGE.REFRESH_TOKEN, encrypted)
  deleteSessionStorage(STORAGE.REFRESH_TOKEN)
}

export const deleteAuthToken = (): void => {
  deleteLocalStorage(STORAGE.TOKEN)
  deleteSessionStorage(STORAGE.TOKEN)
}

export const storage = {
  getLocalStorage,
  setLocalStorage,
  deleteLocalStorage,
  clearLocalStorage,
  getSessionStorage,
  setSessionStorage,
  deleteSessionStorage,
  clearSessionStorage,
  getAuthToken,
  setAuthToken,
  deleteAuthToken,
}
